﻿// ----------------------------------------------------------------------------------------------------
// Linting
// ----------------------------------------------------------------------------------------------------

import { TransitionStyle } from "~base/Styling/Transitions/TransitionGroupTypes";
import { ApiError } from "../API";
import { StepResponse } from "../API/Steps/Model";

/* eslint-disable @typescript-eslint/no-explicit-any */

// Used by mock implementation
/* eslint-disable @typescript-eslint/no-empty-function */

export type UIPart =
    | "Navigation"
    | "BackButton"
    | "ForwardButton"
    | "AlternativeForwardButton"
    | "History"
    | "Panels"
    | "MetaPanel"
    | "ProcessPanel";

export interface IGuideService {
    disableUI(uiPart: UIPart): void;
    enableUI(uiPart: UIPart): void;
    setStep(stepData: StepResponse, readOnlyFromStep?: number): void;
    focusElement(root?: React.MutableRefObject<HTMLElement>): void;
    restoreLastFocus(): void;
    reorderTabs(root?: React.MutableRefObject<HTMLElement>): void;
    unregisterPostAction(id: string);
    clearPostAction(id: string);
    suppressPostAction(id: string);
    registerPostAction(
        id: string,
        value: unknown,
        filter: () => boolean,
        dispatch: React.Dispatch<unknown>
    );
    getDispRes(): unknown;
    updateNextButtonState(value: boolean);
    setStepInput(i: any, a?: any);
    /**
     * Navigate to an existing step.
     * @param stepNumber Step to navigate to.
     * @param transition Transition to use instead of the default.
     */
    goToStep(stepNumber: number, transition?: TransitionStyle): void;
    listServices(sessionID: number, stepNumber: number);
    postService<I, O>(
        sessionID: number,
        stepNumber: number,
        serviceName: string,
        input: I
    );
    frameworkVersion: number;
    setClientError: (error: ApiError) => void;
}

export const guideServiceMock = {
    focusElement: (root?: React.MutableRefObject<HTMLElement>) => {},
    setStep: (stepData: StepResponse, readOnlyFromStep?: number) => {},
    restoreLastFocus: () => {},
    reorderTabs: (root?: React.MutableRefObject<HTMLElement>) => {},
    unregisterPostAction: (id: string) => {},
    clearPostAction: (id: string) => {},
    suppressPostAction: (id: string) => {},
    registerPostAction: (
        id: string,
        value: any,
        filter: () => boolean,
        dispatch: React.Dispatch<unknown>
    ) => {},
    getDispRes: () => {},
    updateNextButtonState: (value: boolean) => {},
    setStepInput: (i: unknown) => {},
    disableUI: (uiPart: UIPart) => {},
    frameworkVersion: 2,
} as IGuideService;

// ----------------------------------------------------------------------------------------------------
// Data model - Views
// ----------------------------------------------------------------------------------------------------

export interface ViewSpec {
    __consilioType__: string;
    guideStegID: string;
    guideStegSubID?: any;
    guideStegÅteranvänd: boolean;
    processPanelID: string;
    processPanelStegID: number;
    metaPanelID: string;
    metaPanelData: any;
    procentKlart: number;
    uppdaterad: boolean;
}

export interface NavigationSpec {
    SessionID: number;
    BranchNumber: number;
    StepNumber: number;
    ReadOnlyFromStepNumber: number;
    SessionBranches: any;
}

export interface InputSpec {
    GLIIA: string;
    Type: string;
    FromThisSessionFlag: boolean;
    FromThisBranchFlag: boolean;
}

export interface TraceInfo {
    WatchedMembers?: any;
}

export interface DispRes {
    DispResultCode: string;
    revision: string;
    ViewID: string;
    SubViewID?: any;
    Locale: string;
    ViewSpec: ViewSpec;
    NavigationSpec: NavigationSpec;
    InputSpec: InputSpec;
    o: any;
    i: any;
    iDef: any;
    iDefPrec: number;
    iPrev: any;
    iPrevPrec?: any;
    InfPartContributions?: any;
    RequestVerificationToken: string;
    TraceInfo: TraceInfo;
}

export interface ConsilioBaseType {
    __consilioType__: string;
    revision: string;
}

// ----------------------------------------------------------------------------------------------------
// Data model - API
// ----------------------------------------------------------------------------------------------------

export interface GuideApiResponseBase {
    statusCode: number;
}

export interface GuideServicesGetResponse extends GuideApiResponseBase {
    _links: any;
    _embedded: any;
    offset: number;
    limit: number;
    totalSize: number;
    items: Array<Service>;
}

export interface Service {
    ServiceName: string;
    InputParameters: Array<ServiceParam>;
}

export interface ServiceParam {
    ParameterName: string;
}

export interface GuideServicesPostResponse<I, O> extends GuideApiResponseBase {
    _links: any;
    _embedded: any;
    Input: I;
    Output: O;
}
